import React from 'react';
import { Link } from 'react-router-dom';
import { Icon, Message } from 'semantic-ui-react';

import styles from './announcementBanner.module.css';

/** A link into the admin, rendered through the router so it does not reload. */
export interface AnnouncementBannerRouteLink {
  label: string;
  to: string;
}

/** A link to another site, opened in a new tab. */
export interface AnnouncementBannerExternalLink {
  label: string;
  href: string;
}

export type AnnouncementBannerLink =
  | AnnouncementBannerRouteLink
  | AnnouncementBannerExternalLink;

export interface AnnouncementBannerProps {
  title: string;
  body?: string;
  /** Rendered inline after the body, or on its own line when there is no body. */
  link?: AnnouncementBannerLink;
  /** When given, a named close button is rendered beside the content. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * `status` announces the banner to screen readers when it appears. Turn it
   * off for a preview that re-renders on every keystroke.
   */
  live?: boolean;
  testId?: string;
  dismissTestId?: string;
  className?: string;
}

const isRouteLink = (
  link: AnnouncementBannerLink
): link is AnnouncementBannerRouteLink => 'to' in link;

/**
 * Upside framed informational banner: a heading, an optional sentence or two,
 * an optional link and an optional dismiss button. First built for the Swiftaid
 * Gift Aid nudge and shared with the PlaceHub program announcement.
 *
 * Deliberately not `record-result`: that class positions a message absolutely
 * over the top of the page, where the payment gateway notice already sits.
 * This one stays in normal flow beneath it. Semantic's own `onDismiss` renders
 * an unnamed, unfocusable icon, so the dismiss control is a real button.
 */
export const AnnouncementBanner = ({
  title,
  body,
  link,
  onDismiss,
  dismissLabel,
  live = true,
  testId,
  dismissTestId,
  className
}: AnnouncementBannerProps) => {
  const linkElement = link ? (
    isRouteLink(link) ? (
      <Link to={link.to} className={styles.link}>
        {link.label}
      </Link>
    ) : (
      <a
        href={link.href}
        className={styles.link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {link.label}
      </a>
    )
  ) : null;

  return (
    <Message
      info
      role={live ? 'status' : undefined}
      className={className}
      data-testid={testId}
    >
      <div className={styles.row}>
        <div className={styles.content}>
          <Message.Header>{title}</Message.Header>
          {(body || linkElement) && (
            <p className={styles.body}>
              {body}
              {body && linkElement ? ' ' : null}
              {linkElement}
            </p>
          )}
        </div>
        {onDismiss && (
          <button
            type="button"
            className={styles.dismiss}
            onClick={onDismiss}
            aria-label={dismissLabel}
            data-testid={dismissTestId}
          >
            <Icon name="close" aria-hidden="true" />
          </button>
        )}
      </div>
    </Message>
  );
};

export default AnnouncementBanner;
